import type { ReactElement } from 'react';
import { MetaStatus } from '@gr/core/content/posts/meta-status';
import type { PostSort, PostSummaryDto } from '@gr/core/content/posts/dto';
import { Link, useT } from '../../../../app/di';
import { formatTime } from '../../../../shared/format';
import { CalendarEntryText, CalendarGrid, CalendarTone, type CalendarEntry } from '../CalendarGrid/CalendarGrid';
import { dayKey, postDate, type CalendarDay, type CalendarView } from '../model/calendar';

export interface PostCalendarProps {
  blogId: string;
  timezone: string;
  view: CalendarView;
  days: readonly CalendarDay[];
  today: CalendarDay;
  /** Месяц на экране: дни соседних месяцев приглушены. */
  month: number;
  /** Посты по дням (groupPostsByDay) — по дате создания или изменения. */
  byDay: ReadonlyMap<string, readonly PostSummaryDto[]>;
  by: PostSort;
  /** Названия статусов блога по id. */
  statusNames: ReadonlyMap<string, string>;
  /** Сколько постов видно в клетке месяца, остальное — «ещё N» (ui.calendar). */
  monthCellLimit?: number;
}

const TONES: Record<MetaStatus, CalendarTone> = {
  [MetaStatus.Draft]: CalendarTone.Neutral,
  [MetaStatus.Ready]: CalendarTone.Accent,
  [MetaStatus.Published]: CalendarTone.Success,
  [MetaStatus.Retired]: CalendarTone.Cancelled,
};

/** SCH-UC-7: посты по дню создания или изменения; время — той же даты, рядом статус поста. */
export function PostCalendar({ blogId, timezone, byDay, by, statusNames, ...grid }: PostCalendarProps): ReactElement {
  const { locale } = useT();
  const entriesOf = (day: CalendarDay): CalendarEntry[] =>
    (byDay.get(dayKey(day)) ?? []).map((post) => ({
      key: post.id,
      tone: TONES[post.meta],
      content: (
        <Link to="post" params={{ blogId, postId: post.id }}>
          <CalendarEntryText time={formatTime(postDate(post, by), locale, timezone)} meta={statusNames.get(post.statusId) ?? ''} title={post.title} />
        </Link>
      ),
    }));
  return <CalendarGrid {...grid} entriesOf={entriesOf} />;
}
